import { supabase } from '@/integrations/supabase/client';
import { ProductEventsApiError, type PageEventPayload, type RecordPageEventsResult } from '@/types/productEvents';

// FIN-1154 PE-R14: posts the consultant app's page events to product-events-api. A plain fetch
// with `keepalive`, not supabase.functions.invoke: a batch sent as the tab is hidden or closed
// must outlive the page, and invoke() has no keepalive option. sendBeacon cannot carry the
// Authorization header the function needs.
export class ProductEventsApiClient {
  async recordPageEvents(events: PageEventPayload[], options: { keepalive?: boolean } = {}): Promise<RecordPageEventsResult> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new ProductEventsApiError('Not signed in', 401, false);

    let response: Response;
    try {
      response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/product-events-api`, {
        method: 'POST',
        keepalive: options.keepalive ?? false,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'recordPageEvents', events }),
      });
    } catch {
      throw new ProductEventsApiError('Page events could not reach the server', null, true);
    }

    if (!response.ok) {
      const retryable = response.status === 429 || response.status >= 500;
      throw new ProductEventsApiError(`Page events were refused (${response.status})`, response.status, retryable);
    }
    return response.json() as Promise<RecordPageEventsResult>;
  }
}
